/**
 * Tenant provisioning hooks — Wave 1a / W1a-19.
 *
 * Three hooks covering the two W1a-19 flows:
 *   - useProvisionTenant() — sysadmin-initiated tenant create. Posts to
 *     `/api/v1/admin/platform/tenants` with the multi-step wizard payload.
 *   - useValidateInstallToken(token) — public, no-auth GET against
 *     `/api/v1/setup/install-token/:token`. Used by the /setup/<token>
 *     page on mount to gate the form.
 *   - useRedeemInstall() — public, no-auth POST to `/api/v1/setup/install`
 *     that enrolls the first sysadmin + provisions the first tenant.
 *
 * The two install endpoints DO NOT carry an auth header — the install
 * token is the auth. They use raw fetch rather than apiClient (which
 * always attaches Authorization).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Wire types ──────────────────────────────────────────────────────────────

export type TenantMode = 'iq_connected' | 'standalone';
export type ComplianceProfile =
  | 'soc2'
  | 'iso27001'
  | 'itar'
  | 'fedramp'
  | 'hipaa'
  | 'none';
export type SsoProvider = 'oidc' | 'saml' | 'google' | 'entra';

export interface ProvisionTenantBody {
  // Step 1 — Basic
  name: string;
  slug: string;
  orgCode: string;
  mode?: TenantMode;

  // Step 2 — SSO config (optional)
  sso?: {
    provider: SsoProvider;
    clientId?: string;
    tenantId?: string;
    discoveryUrl?: string;
  };

  // Step 3 — the two tenant settings, keyed `allow_web_search` /
  // `allow_external_connections`. The field keeps its old name so clients do
  // not change shape in the same release that changed its meaning.
  features?: Record<string, boolean>;

  // Step 4 — compliance profile
  complianceProfile?: ComplianceProfile;

  // Step 5 — resource limits
  resourceLimits?: {
    maxUsers?: number;
    maxStorageGb?: number;
    maxMonthlyTokens?: number;
  };

  // Step 6 — first tenant-admin invite
  firstAdmin?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface ProvisionTenantResponse {
  tenant: {
    id: string;
    name: string;
    slug: string;
    orgCode: string;
    mode: TenantMode;
    status: 'pending';
  };
  invitation: {
    token: string;
    expiresAt: string;
    inviteUrl: string;
  } | null;
  signingPolicyReconciled: boolean;
}

// ─── Sysadmin: useProvisionTenant ────────────────────────────────────────────

export function useProvisionTenant() {
  const qc = useQueryClient();
  return useMutation<ProvisionTenantResponse, Error, ProvisionTenantBody>({
    mutationFn: (body) =>
      apiClient.post<ProvisionTenantResponse>(
        '/admin/platform/tenants',
        body as unknown as Record<string, unknown>
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-overview'] });
    },
  });
}

// ─── Public: install-token validation ────────────────────────────────────────

export interface InstallTokenValidation {
  valid: true;
  expiresAt: string;
}

export interface InstallTokenError {
  code: 'INSTALL_TOKEN_NOT_FOUND' | 'INSTALL_TOKEN_EXPIRED' | 'INSTALL_TOKEN_CONSUMED';
  message: string;
}

const API_BASE = '/api/v1';

/**
 * Validate an install token. Returns 200 + { valid, expiresAt } on success.
 * Throws on 404 / 410 with a structured error message including the code,
 * so the UI can render a "Token expired" / "Already used" page.
 */
export function useValidateInstallToken(token: string | undefined) {
  return useQuery<InstallTokenValidation, Error & { code?: string }>({
    queryKey: ['install-token', token],
    queryFn: async () => {
      if (!token) throw new Error('Missing token');
      const res = await fetch(`${API_BASE}/setup/install-token/${token}`, {
        headers: { Accept: 'application/json' },
      });
      const text = await res.text();
      if (!res.ok) {
        let code = 'INSTALL_TOKEN_INVALID';
        let message = 'Install token validation failed';
        try {
          const body = JSON.parse(text) as { error?: { code?: string; message?: string } };
          code = body.error?.code ?? code;
          message = body.error?.message ?? message;
        } catch {
          /* keep defaults */
        }
        const err = new Error(message) as Error & { code?: string };
        err.code = code;
        throw err;
      }
      return JSON.parse(text) as InstallTokenValidation;
    },
    enabled: !!token,
    retry: false,
  });
}

// ─── Public: install redemption ──────────────────────────────────────────────

export interface RedeemInstallBody {
  token: string;
  sysadmin: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  };
  firstTenant: Omit<ProvisionTenantBody, 'firstAdmin'>;
  totpEnrollmentRequest?: boolean;
}

export interface RedeemInstallResponse {
  sysadmin: {
    id: string;
    email: string;
    totpSecret: string | null;
    qrUri: string | null;
  };
  tenant: ProvisionTenantResponse['tenant'];
  invitation: ProvisionTenantResponse['invitation'];
  signingPolicyReconciled: boolean;
}

export function useRedeemInstall() {
  return useMutation<
    RedeemInstallResponse,
    Error & { code?: string },
    RedeemInstallBody
  >({
    mutationFn: async (body) => {
      const res = await fetch(`${API_BASE}/setup/install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      if (!res.ok) {
        let code = 'INSTALL_FAILED';
        let message = 'Install redemption failed';
        try {
          const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
          code = parsed.error?.code ?? code;
          message = parsed.error?.message ?? message;
        } catch {
          /* keep defaults */
        }
        const err = new Error(message) as Error & { code?: string };
        err.code = code;
        throw err;
      }
      return JSON.parse(text) as RedeemInstallResponse;
    },
  });
}
